import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-menu-button WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, MenuButtonWebElement.ts.
 */
export class MenuButtonWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>label</code> property.
   * Text to show in the button.
   * @return The value of <code>label</code> property.
   *
   */
  public getLabel(): Promise<string> {
    return this.getProperty<string>('label');
  }

  /**
   * Gets the value of <code>suffix</code> property.
   * Suffix appended to menu label to indicate last selection.
   * @return The value of <code>suffix</code> property.
   *
   */
  public getSuffix(): Promise<string> {
    return this.getProperty<string>('suffix');
  }

  /**
   * Gets the value of <code>tooltip</code> property.
   * Text to show in the tooltip. This overrides the default tooltip that renders the label when in icon mode.
   * @return The value of <code>tooltip</code> property.
   *
   */
  public getTooltip(): Promise<string> {
    return this.getProperty<string>('tooltip');
  }

  /**
   * Gets the value of <code>items</code> property.
   * Items describe the menu items rendered by the menu button.
   * @return The value of <code>items</code> property.
   *
   */
  public getItems(): Promise<Array<object>> {
    return this.getProperty<Array<object>>('items');
  }

  /**
   * Sets the value of <code>selection</code> property.
   * An array containing key/value objects for menu selection groups.
   * @param selection The value to set for <code>selection</code>
   *
   */
  public changeSelection(selection: object): Promise<void> {
    return this.setProperty<object>('selection', selection);
  }

  /**
   * Gets the value of <code>selection</code> property.
   * An array containing key/value objects for menu selection groups.
   * @return The value of <code>selection</code> property.
   *
   */
  public getSelection(): Promise<object> {
    return this.getProperty<object>('selection');
  }

  /**
   * Gets the value of <code>display</code> property.
   * Display just the label, the icons, or all. Label is used as tooltip and should be set in all cases.
   * @return The value of <code>display</code> property.
   *
   */
  public getDisplay(): Promise<string> {
    return this.getProperty<string>('display');
  }

  /**
   * Gets the value of <code>disabled</code> property.
   * Specifies that the button element should be disabled.
   * @return The value of <code>disabled</code> property.
   *
   */
  public getDisabled(): Promise<boolean> {
    return this.getProperty<boolean>('disabled');
  }

  /**
   * Gets the value of <code>size</code> property.
   * Size of button
   * @return The value of <code>size</code> property.
   *
   */
  public getSizeProperty(): Promise<string> {
    return this.getProperty<string>('size');
  }

  /**
   * Gets the value of <code>width</code> property.
   * Specifies that the button style width
   * @return The value of <code>width</code> property.
   *
   */
  public getWidth(): Promise<number | string> {
    return this.getProperty<number | string>('width');
  }

  /**
   * Gets the value of <code>chroming</code> property.
   * Indicates in what states the button has chromings in background and border.
   * @return The value of <code>chroming</code> property.
   *
   */
  public getChroming(): Promise<string> {
    return this.getProperty<string>('chroming');
  }
}
